import { type MonthKPIData } from '../widgets/hooks'
import dayjs from 'dayjs'

export interface InsightResult {
  text: string
  topPerformer: {
    metric_id: number
    key: string
    name: string
  } | null
  bottomPerformer: {
    metric_id: number
    key: string
    name: string
  } | null
}

export function getInsight(
  monthData: MonthKPIData,
  onlyTopPerformer: boolean = false
): InsightResult {
  const { kpis, current_period } = monthData

  // Filter out KPIs with null values
  const validKpis = kpis.filter(
    (kpi) => kpi.current_month !== null && kpi.previous_month !== null
  )

  if (validKpis.length === 0) {
    return {
      text: 'No KPI data available for analysis.',
      topPerformer: null,
      bottomPerformer: null,
    }
  }

  // Get the full month name from the current period
  const monthName = dayjs(current_period).format('MMMM')

  // Calculate changes and sort by current month performance
  const kpisWithChange = validKpis.map((kpi) => ({
    ...kpi,
    change: kpi.current_month! - kpi.previous_month!, // Simple subtraction since values are already percentages
    ytd: kpi.ytd,
  }))

  // Sort by current month value to find top and bottom performers
  const sortedByPerformance = [...kpisWithChange].sort(
    (a, b) => b.current_month! - a.current_month!
  )
  const topPerformer = sortedByPerformance[0]
  const bottomPerformer = sortedByPerformance[sortedByPerformance.length - 1]

  // Build the insight text using the template:
  // "In {October}, {ATV} was the leading KPI at {% value}, {up/down} {value variance} compared to the previous month.
  // Meanwhile, {Transactions} requires more focus, performing at {% value}, with a {value variance} month-on-month change.
  // With {October} complete, YTD shows {ATV} at {94.1%}, and {Transactions} at {89.3%}."
  let insight = ''

  // Top performer section
  if (topPerformer) {
    const topValue = (topPerformer.current_month! * 100).toFixed(1)
    const topChangeDirection = topPerformer.change >= 0 ? 'up' : 'down'
    const topChangeValue = Math.abs(topPerformer.change * 100).toFixed(1)

    insight += `In ${monthName}, ${topPerformer.name} was the leading KPI at ${topValue}%, ${topChangeDirection} ${topChangeValue}% compared to the previous month.`
  }

  // Bottom performer section (only if different from top performer)
  if (
    !onlyTopPerformer &&
    bottomPerformer &&
    bottomPerformer.metric_id !== topPerformer.metric_id
  ) {
    const bottomValue = (bottomPerformer.current_month! * 100).toFixed(1)
    const bottomChangeValue = Math.abs(bottomPerformer.change * 100).toFixed(1)

    insight += ` Meanwhile, ${bottomPerformer.name} requires more focus, performing at ${bottomValue}%, with a ${bottomChangeValue}% month-on-month change.`
  }

  // YTD section
  if (
    topPerformer &&
    topPerformer.ytd !== null &&
    topPerformer.ytd !== undefined
  ) {
    const topYtd = (topPerformer.ytd * 100).toFixed(1)

    if (
      !onlyTopPerformer &&
      bottomPerformer &&
      bottomPerformer.metric_id !== topPerformer.metric_id &&
      bottomPerformer.ytd !== null &&
      bottomPerformer.ytd !== undefined
    ) {
      const bottomYtd = (bottomPerformer.ytd * 100).toFixed(1)
      insight += ` With ${monthName} complete, YTD shows ${topPerformer.name} at ${topYtd}%, and ${bottomPerformer.name} at ${bottomYtd}%.`
    } else {
      insight += ` With ${monthName} complete, YTD shows ${topPerformer.name} at ${topYtd}%.`
    }
  }

  return {
    text: insight,
    topPerformer: topPerformer
      ? {
          metric_id: topPerformer.metric_id,
          key: topPerformer.key,
          name: topPerformer.name,
        }
      : null,
    bottomPerformer: bottomPerformer
      ? {
          metric_id: bottomPerformer.metric_id,
          key: bottomPerformer.key,
          name: bottomPerformer.name,
        }
      : null,
  }
}
